/**
 * Voice guide for face capture: step, lighting, capture guidance, aligned, captured.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  guidanceToSpeech,
  lightingToSpeech,
  speakNaturally,
  stepToSpeech,
} from '../utils/speechGuide';

const GUIDANCE_THROTTLE_MS = 4500;
const LIGHTING_THROTTLE_MS = 5500;

export function useVoiceGuide(guidedStep, isAligned, isCapturing, enabled = true, lightingOk = true, lightingMessage = '', guidanceMessage = '', lightingReady = true) {
  const lastStepRef = useRef(null);
  const lastAlignedRef = useRef(false);
  const lastCapturingRef = useRef(false);
  const lastGuidanceRef = useRef('');
  const lastLightingRef = useRef(true);
  const lastGuidanceTimeRef = useRef(0);
  const lastLightingTimeRef = useRef(0);
  const enabledRef = useRef(enabled);
  const capturedSpeechTimeoutRef = useRef(null);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const check = () => window.speechSynthesis.getVoices().length > 0;
    if (!check()) {
      window.speechSynthesis.onvoiceschanged = () => {
        check();
        window.speechSynthesis.onvoiceschanged = null;
      };
      const t = setInterval(() => { if (check()) clearInterval(t); }, 100);
      return () => { clearInterval(t); window.speechSynthesis.onvoiceschanged = null; };
    }
    return undefined;
  }, []);

  const speak = useCallback((text, opts = {}) => {
    speakNaturally(text, opts);
  }, []);

  const unlockForIOS = useCallback((textToSpeak) => {
    const fallback = stepToSpeech(guidedStep) || 'Voice guide is on. Let us get your face aligned.';
    const text = (textToSpeak || fallback).trim();
    if (!text) return;
    try {
      speak(text, { rate: 0.92, pitch: 1.02 });
    } catch (_) { /* ignore */ }
  }, [guidedStep, speak]);

  useEffect(() => {
    if (!enabled && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, [enabled]);

  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.speechSynthesis) return;
    if (lastStepRef.current === guidedStep) return;
    const text = stepToSpeech(guidedStep);
    if (!text) return;
    lastStepRef.current = guidedStep;
    speak(text);
  }, [guidedStep, speak, enabled]);

  useEffect(() => {
    if (!enabled || !isAligned || lastAlignedRef.current) return;
    lastAlignedRef.current = true;
    speak(stepToSpeech('aligned'));
  }, [isAligned, speak, enabled]);

  useEffect(() => {
    if (!isAligned) lastAlignedRef.current = false;
  }, [isAligned]);

  useEffect(() => {
    if (!enabled || !isCapturing || lastCapturingRef.current) return;
    lastCapturingRef.current = true;
    if (capturedSpeechTimeoutRef.current) clearTimeout(capturedSpeechTimeoutRef.current);
    capturedSpeechTimeoutRef.current = setTimeout(() => {
      capturedSpeechTimeoutRef.current = null;
      if (enabledRef.current) speak(stepToSpeech('captured'));
    }, 120);
    return () => {
      if (capturedSpeechTimeoutRef.current) {
        clearTimeout(capturedSpeechTimeoutRef.current);
        capturedSpeechTimeoutRef.current = null;
      }
    };
  }, [isCapturing, speak, enabled]);

  useEffect(() => {
    if (!isCapturing) lastCapturingRef.current = false;
  }, [isCapturing]);

  useEffect(() => {
    if (!enabled || !lightingReady || typeof window === 'undefined' || !window.speechSynthesis) return;
    if (isAligned || isCapturing) return;
    if (lightingOk) {
      lastLightingRef.current = true;
      return;
    }
    const now = Date.now();
    if (lastLightingRef.current !== false || now - lastLightingTimeRef.current >= LIGHTING_THROTTLE_MS) {
      lastLightingRef.current = false;
      lastLightingTimeRef.current = now;
      speak(lightingToSpeech(lightingMessage));
    }
  }, [enabled, lightingReady, lightingOk, lightingMessage, isAligned, isCapturing, speak]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.speechSynthesis) return;
    if (isAligned || isCapturing || !lightingOk) return;
    const msg = String(guidanceMessage || '').trim();
    if (!msg) return;
    const voiceText = guidanceToSpeech(msg);
    const now = Date.now();
    if (voiceText && voiceText !== lastGuidanceRef.current && now - lastGuidanceTimeRef.current >= GUIDANCE_THROTTLE_MS) {
      lastGuidanceRef.current = voiceText;
      lastGuidanceTimeRef.current = now;
      speak(voiceText);
    }
  }, [enabled, guidanceMessage, isAligned, isCapturing, lightingOk, speak]);

  return { speak, unlockForIOS };
}
